import { Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import InHouseBarChart from "../../../../../components/UIToolkit/Graphs/BarChart/InHouseBarChart";
import type { InHouseBarDataGroup } from "../../../../../components/UIToolkit/Graphs/BarChart/types";
import { COST_VARIANCE } from "../../../../../theme/tokens";
import type { ShiftsDailyPoint } from "../../../../../utils/models";
import { SectionHeading } from "./SectionHeading";

/**
 * The two kinds of mismatch side by side per schedule day, so a day the punches didn't arrive stands
 * out as a No punches spike. Colors are the scheduled/actual provenance pair the cost-variance chart
 * uses for the same meaning (a no-punch gap is a scheduled-side miss, an unscheduled shift actual-side).
 */
export const ShiftsDailyChart: FC<{ daily: ShiftsDailyPoint[] }> = ({ daily }) => {
	const { t } = useTranslation();
	const stackedGroups = useMemo<InHouseBarDataGroup[][]>(
		() => [
			[
				{
					name: t("integration.summary.shifts.stat.noPunches"),
					color: COST_VARIANCE.NO_PUNCH,
					data: daily.map((point) => ({ dateOrLabel: point.day, value: point.scheduledWithoutActualCount })),
				},
			],
			[
				{
					name: t("integration.summary.shifts.stat.noSchedules"),
					color: COST_VARIANCE.UNPLANNED,
					data: daily.map((point) => ({ dateOrLabel: point.day, value: point.actualWithoutScheduleCount })),
				},
			],
		],
		[daily, t],
	);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="hris-shifts-summary-daily-chart">
			<SectionHeading title={t("integration.summary.shifts.daily.title")} description={t("integration.summary.shifts.daily.description")} />
			<InHouseBarChart stackedGroups={stackedGroups} width="100%" visibilityToggling={false} />
		</Box>
	);
};
